import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { ActionMenu } from './action-menu.js';
import { IconButton } from './button.js';
const STORAGE_KEY = 'kanos.theme';
const media = () => window.matchMedia('(prefers-color-scheme: dark)');
function readPreference() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === 'light' || stored === 'dark' ? stored : 'system';
    }
    catch {
        return 'system';
    }
}
function apply(preference) {
    const dark = preference === 'dark' || (preference === 'system' && media().matches);
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}
/**
 * Inline script for <head>: sets the theme before the first render (no flash).
 * Allow it by hash under a strict CSP.
 */
export const themeBootScript = `try{var p=localStorage.getItem('${STORAGE_KEY}');var d=p==='dark'||(p!=='light'&&matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.dataset.theme=d?'dark':'light'}catch(e){}`;
export function useTheme() {
    const [preference, setPreference] = useState(readPreference);
    useEffect(() => {
        apply(preference);
        if (preference !== 'system')
            return;
        const query = media();
        const onChange = () => {
            apply('system');
        };
        query.addEventListener('change', onChange);
        return () => {
            query.removeEventListener('change', onChange);
        };
    }, [preference]);
    const update = useCallback((next) => {
        try {
            if (next === 'system')
                localStorage.removeItem(STORAGE_KEY);
            else
                localStorage.setItem(STORAGE_KEY, next);
        }
        catch {
            // Storage unavailable (e.g. private mode): the choice only lasts for this session.
        }
        setPreference(next);
    }, []);
    return { preference, setPreference: update };
}
/** The three ways to show Kanos, with their symbols - for the account menu and `ThemeMenu`. */
export const THEME_CHOICES = [
    { id: 'system', label: 'Wie das Gerät', icon: Monitor },
    { id: 'light', label: 'Hell', icon: Sun },
    { id: 'dark', label: 'Dunkel', icon: Moon },
];
/** The symbol of a choice, e.g. on the button that opens it. */
export const themeIcon = (preference) => THEME_CHOICES.find((choice) => choice.id === preference)?.icon ?? Monitor;
/** The symbol of the current choice, as its own element (not a component made in render). */
function ThemeIcon({ preference }) {
    if (preference === 'dark')
        return _jsx(Moon, { "aria-hidden": true, className: "size-5" });
    if (preference === 'light')
        return _jsx(Sun, { "aria-hidden": true, className: "size-5" });
    return _jsx(Monitor, { "aria-hidden": true, className: "size-5" });
}
/**
 * Light or dark where nobody is signed in (the portal before an account): a round button with
 * the symbol of the current choice, the three choices in a menu under it. Signed in, the
 * choice lives in the account menu.
 */
export function ThemeMenu() {
    const { preference, setPreference } = useTheme();
    return (_jsx(ActionMenu, { label: "Darstellung", trigger: _jsx(IconButton, { "aria-label": "Darstellung", children: _jsx(ThemeIcon, { preference: preference }) }), groups: [
            {
                id: 'theme',
                title: 'Darstellung',
                selected: preference,
                onSelect: (id) => setPreference(id),
                items: THEME_CHOICES,
            },
        ] }));
}
//# sourceMappingURL=theme.js.map